// =============================================================================
// client/src/pages/PrintOrderPage.js
// =============================================================================
// Picking list and dispatch note for printing / PDF generation.
//
// Triggered by adding p=true to the order editor URL, or via the Print button:
//   /order-editor?hubspotOrderId=...&storeId=...&p=true
//   /order-editor?orderId=42&p=true
//
// SECTION 1 -- Picking List (for warehouse staff)
//   All physical items including pack contents, with checkbox per line.
//
// SECTION 2 -- Dispatch / Delivery Note (signed by customer on delivery)
//   Top-level lines only, with pricing, totals, payment method, signature block.
// =============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { getOrder, findOrderByHubSpotOrderId, downloadOrderPdf } from '../api';

const vatRate  = (taxCode) => (taxCode === 'A0' ? 0 : 0.20);
const discFrac = (pct) => (parseFloat(pct) || 0) / 100;
const lineNet  = (line) =>
  (line.Quantity || 0) * parseFloat(line.UnitPrice || 0) * (1 - discFrac(line.DiscountPct));

function DocHeader({ title, order }) {
  const printDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: 18, paddingBottom: 12, borderBottom: '2px solid #000',
    }}>
      <img
        src="/oakhouse-logo.png"
        alt="Oakhouse Foods"
        style={{ width: 80, height: 80, objectFit: 'contain' }}
        onError={e => { e.target.style.display = 'none'; }}
      />
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 22, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</div>
        <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.8 }}>
          <strong>Order:</strong> {order?.OrderNumber || 'n/a'}<br />
          <strong>Date:</strong> {printDate}<br />
          <strong>Site:</strong> {order?.Site?.SiteName || order?.Site?.SiteCode || 'n/a'}
        </div>
      </div>
    </div>
  );
}

function PickingList({ order }) {
  const allLines = order?.Lines || [];

  // Collect every line that references a physical product (standalone + inside packs).
  // Pack header lines (PackId set, no ProductId) are skipped — they are just grouping.
  const productLines = allLines.filter(l => l.ProductId);

  // Group by effective SKU, summing quantities.
  // For substituted lines the physical item to pick is the substitute.
  const grouped = new Map();
  for (const line of productLines) {
    const isSubstituted = line.IsSubstituted && (line.SubstituteProduct || line.SubstitutionCode);
    const sku  = isSubstituted
      ? (line.SubstituteProduct?.SKU || line.SubstitutionCode || line.Product?.SKU || '')
      : (line.Product?.SKU || '');
    const name = isSubstituted
      ? (line.SubstituteProduct?.ProductName || line.SubstitutionCode || sku)
      : (line.Product?.ProductName || sku || 'Product');
    const key = sku || name;
    if (grouped.has(key)) {
      grouped.get(key).qty += (line.Quantity || 0);
      if (isSubstituted) grouped.get(key).substituted = true;
    } else {
      grouped.set(key, { sku, name, qty: line.Quantity || 0, substituted: !!isSubstituted });
    }
  }

  const rows = [...grouped.values()].sort((a, b) => a.sku.localeCompare(b.sku));

  const cell = { padding: '5px 8px', borderBottom: '1px solid #ccc', verticalAlign: 'middle', fontSize: 12 };
  const head = { padding: '7px 8px', background: '#000', color: '#fff', textAlign: 'left', fontSize: 12, fontWeight: 700 };

  return (
    <div>
      <DocHeader title="Picking List" order={order} />
      {order?.Notes && (
        <div style={{ border: '1px solid #ccc', padding: '6px 10px', marginBottom: 12, fontSize: 12 }}>
          <strong>Notes:</strong> {order.Notes}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 20 }}>
        <thead>
          <tr>
            <th style={{ ...head, width: 38 }}>#</th>
            <th style={{ ...head, width: 110 }}>SKU</th>
            <th style={head}>Description</th>
            <th style={{ ...head, width: 55, textAlign: 'center' }}>Total Qty</th>
            <th style={{ ...head, width: 65, textAlign: 'center' }}>Picked</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.sku || i}>
              <td style={cell}>{i + 1}</td>
              <td style={{ ...cell, fontFamily: 'monospace' }}>{row.sku}</td>
              <td style={cell}>
                {row.name}
                {row.substituted && <span style={{ marginLeft: 8, fontSize: 10 }}>[Substituted]</span>}
              </td>
              <td style={{ ...cell, textAlign: 'center', fontWeight: 700 }}>{row.qty}</td>
              <td style={{ ...cell, textAlign: 'center' }}>
                <span style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid #000', borderRadius: 2 }}></span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 32, marginTop: 20, paddingTop: 10, borderTop: '1px solid #000', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
        {['Picked by (print name)', 'Date and Time', 'Checked by'].map(label => (
          <div key={label} style={{ flex: 1 }}>
            <div style={{ fontSize: 11, marginBottom: 4 }}>{label}</div>
            <div style={{ borderBottom: '1px solid #000', height: 22 }}></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DispatchNote({ order }) {
  const lines = (order?.Lines || []).filter(l => !l.PackHeaderLineId);
  const grossTotal  = lines.reduce((s, l) => s + lineNet(l), 0);
  const discountPct = parseFloat(order?.DiscountPct || 0);
  const discountAmt = grossTotal * discFrac(discountPct);
  const netTotal    = grossTotal - discountAmt;
  const totalVat    = lines.reduce((s, l) => {
    const vr = vatRate(l.TaxCode);
    return s + lineNet(l) * vr / (1 + vr);
  }, 0);

  const cell = { padding: '5px 8px', borderBottom: '1px solid #ccc', verticalAlign: 'middle', fontSize: 12 };
  const head = { padding: '7px 8px', background: '#000', color: '#fff', textAlign: 'left', fontSize: 12, fontWeight: 700 };

  return (
    <div>
      <DocHeader title="Dispatch Note" order={order} />
      <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
        <div style={{ flex: 1, border: '1px solid #ccc', padding: '8px 12px', fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11, textTransform: 'uppercase' }}>Deliver To</div>
          <div style={{ fontWeight: 600 }}>{order?.Site?.SiteName || 'n/a'}</div>
          {order?.Site?.SiteCode && <div>Site Code: {order.Site.SiteCode}</div>}
          {order?.Site?.SiteId   && <div>Site ID: {order.Site.SiteId}</div>}
        </div>
        <div style={{ flex: 1, border: '1px solid #ccc', padding: '8px 12px', fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 11, textTransform: 'uppercase' }}>Order Details</div>
          <div>Status: {order?.Status || 'n/a'}</div>
          {order?.HubSpotDealId && <div>Deal ID: {order.HubSpotDealId}</div>}
          {order?.WarehouseId   && <div>Warehouse: {order.WarehouseId}</div>}
          {order?.Notes         && <div>Notes: {order.Notes}</div>}
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 14 }}>
        <thead>
          <tr>
            <th style={{ ...head, width: 34 }}>#</th>
            <th style={head}>Product / Pack</th>
            <th style={{ ...head, width: 50, textAlign: 'center' }}>Qty</th>
            <th style={{ ...head, width: 85, textAlign: 'right' }}>Unit Price</th>
            {discountPct > 0 && <th style={{ ...head, width: 70, textAlign: 'right' }}>Discount</th>}
            <th style={{ ...head, width: 90, textAlign: 'right' }}>Total inc. VAT</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => {
            const name = line.PackId && !line.ProductId
              ? (line.Pack?.PackName || line.Pack?.PackSKU || 'Pack')
              : (line.IsSubstituted
                  ? (line.SubstituteProduct?.ProductName || line.SubstitutionCode || 'Item') + ' [Substituted]'
                  : (line.Product?.ProductName || line.Pack?.PackName || 'Item'));
            const sku   = line.Product?.SKU || line.Pack?.PackSKU || '';
            const total = lineNet(line);
            const disc  = (line.Quantity || 0) * parseFloat(line.UnitPrice || 0) * discFrac(line.DiscountPct);
            return (
              <tr key={line.LineId}>
                <td style={cell}>{i + 1}</td>
                <td style={cell}>
                  {name}
                  {sku && <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 10 }}>{sku}</span>}
                </td>
                <td style={{ ...cell, textAlign: 'center' }}>{line.Quantity}</td>
                <td style={{ ...cell, textAlign: 'right', fontFamily: 'monospace' }}>£{parseFloat(line.UnitPrice || 0).toFixed(2)}</td>
                {discountPct > 0 && (
                  <td style={{ ...cell, textAlign: 'right', fontFamily: 'monospace' }}>
                    {disc > 0 ? '-£' + disc.toFixed(2) : ''}
                  </td>
                )}
                <td style={{ ...cell, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>£{total.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: 260 }}>
          <tbody>
            {discountPct > 0 && (
              <>
                <tr>
                  <td style={{ padding: '3px 10px', textAlign: 'right' }}>Gross Total (inc. VAT)</td>
                  <td style={{ padding: '3px 10px', textAlign: 'right', fontFamily: 'monospace', width: 90 }}>£{(grossTotal + discountAmt).toFixed(2)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '3px 10px', textAlign: 'right' }}>Discount ({discountPct}%)</td>
                  <td style={{ padding: '3px 10px', textAlign: 'right', fontFamily: 'monospace' }}>-£{discountAmt.toFixed(2)}</td>
                </tr>
              </>
            )}
            <tr>
              <td style={{ padding: '3px 10px', textAlign: 'right' }}>Net Amount (inc. VAT)</td>
              <td style={{ padding: '3px 10px', textAlign: 'right', fontFamily: 'monospace' }}>£{netTotal.toFixed(2)}</td>
            </tr>
            <tr>
              <td style={{ padding: '3px 10px', textAlign: 'right' }}>VAT Amount</td>
              <td style={{ padding: '3px 10px', textAlign: 'right', fontFamily: 'monospace' }}>£{totalVat.toFixed(2)}</td>
            </tr>
            <tr style={{ borderTop: '2px solid #000' }}>
              <td style={{ padding: '6px 10px', fontWeight: 900, fontSize: 14, textAlign: 'right' }}>TOTAL (inc. VAT)</td>
              <td style={{ padding: '6px 10px', fontWeight: 900, fontSize: 14, textAlign: 'right', fontFamily: 'monospace' }}>£{netTotal.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ border: '1px solid #ccc', padding: '10px 12px', marginBottom: 16, pageBreakInside: 'avoid', breakInside: 'avoid' }}>
        <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 11, textTransform: 'uppercase' }}>Payment Method</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
          {['Cash', 'Invoice (30 days)', 'Card', 'BACS / Transfer'].map(method => (
            <label key={method} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid #000', borderRadius: 2 }}></span>
              {method}
            </label>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 140 }}>
            <span>Other:</span>
            <span style={{ flex: 1, borderBottom: '1px solid #000', height: 14 }}></span>
          </div>
        </div>
      </div>
      <div style={{ border: '1px solid #000', padding: '12px 14px', pageBreakInside: 'avoid', breakInside: 'avoid' }}>
        <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 11, textTransform: 'uppercase' }}>
          Customer Confirmation - Please sign to confirm receipt of goods
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 28px' }}>
          {['Name (print)', 'Signature', 'Date', 'Comments / Issues'].map(label => (
            <div key={label}>
              <div style={{ fontSize: 11, marginBottom: 3 }}>{label}</div>
              <div style={{ borderBottom: '1px solid #000', height: label === 'Signature' || label === 'Comments / Issues' ? 28 : 22 }}></div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 12, textAlign: 'center', fontSize: 10, borderTop: '1px solid #ccc', paddingTop: 6, pageBreakInside: 'avoid', breakInside: 'avoid' }}>
        Oakhouse Foods - Established 1994
      </div>
    </div>
  );
}

export default function PrintOrderPage() {
  const params       = new URLSearchParams(window.location.search);
  const urlOrderId   = params.get('orderId');
  const urlHsOrderId = params.get('hubspotOrderId');
  const urlStoreId   = params.get('storeId');

  const [order, setOrder]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [printing, setPrinting]   = useState(false);
  const [pdfBusy, setPdfBusy]     = useState(false);
  const [resolvedId, setResolvedId] = useState(null);

  const handlePrint = useCallback(() => {
    setPrinting(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.print();
        setPrinting(false);
      });
    });
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    if (!resolvedId) return;
    setPdfBusy(true);
    try {
      const res = await downloadOrderPdf(resolvedId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      alert('PDF generation failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setPdfBusy(false);
    }
  }, [resolvedId]);

  const loadOrder = useCallback(async () => {
    try {
      let orderId = urlOrderId ? parseInt(urlOrderId, 10) : null;
      if (!orderId && urlHsOrderId) {
        const lookupRes = await findOrderByHubSpotOrderId(urlHsOrderId, urlStoreId || null);
        orderId = lookupRes.data?.OrderId;
      }
      if (!orderId) {
        setError('No order found. The URL must contain an orderId or hubspotOrderId parameter.');
        return;
      }
      const orderRes = await getOrder(orderId);
      setOrder(orderRes.data);
      setResolvedId(orderId);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [urlOrderId, urlHsOrderId, urlStoreId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>Loading order...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ border: '1px solid #ccc', borderRadius: 6, padding: 28, maxWidth: 440, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Could not load order</div>
          <div style={{ fontSize: 13 }}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
          .print-page { padding: 10mm 14mm; }
          .page-break { page-break-after: always; break-after: page; }
          @page { margin: 10mm 12mm; size: A4; }
        }
        @media screen {
          body { background: #e8e8e8; font-family: Arial, sans-serif; }
          .print-page {
            background: #fff;
            max-width: 210mm;
            margin: 0 auto;
            padding: 18mm 22mm;
            box-shadow: 0 2px 12px rgba(0,0,0,.18);
          }
        }
      `}</style>

      <div className="no-print" style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: '#111', color: '#fff',
        padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontFamily: 'Arial, sans-serif',
      }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>
          Print Preview - {order?.OrderNumber} - {order?.Site?.SiteName}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={handleDownloadPdf}
            disabled={pdfBusy || !resolvedId}
            style={{ padding: '7px 18px', background: '#4caf50', color: '#fff', border: 'none', borderRadius: 3, fontWeight: 700, fontSize: 13, cursor: (pdfBusy || !resolvedId) ? 'default' : 'pointer', opacity: (pdfBusy || !resolvedId) ? 0.7 : 1 }}
          >
            {pdfBusy ? 'Generating…' : 'Download PDF'}
          </button>
          <button
            onClick={handlePrint}
            disabled={printing}
            style={{ padding: '7px 18px', background: '#fff', color: '#111', border: 'none', borderRadius: 3, fontWeight: 700, fontSize: 13, cursor: printing ? 'default' : 'pointer', opacity: printing ? 0.7 : 1 }}
          >
            {printing ? 'Preparing…' : 'Browser Print'}
          </button>
          <button
            onClick={() => window.close()}
            style={{ padding: '7px 14px', background: 'transparent', color: '#fff', border: '1px solid #555', borderRadius: 3, fontSize: 12, cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
      </div>

      <div style={{ paddingBottom: 40 }}>
        <div className="print-page">
          <PickingList order={order} />
        </div>
        <div className="page-break"></div>
        <div className="print-page" style={{ marginTop: 16 }}>
          <DispatchNote order={order} />
        </div>
      </div>
    </>
  );
}
